import "./App.css"
import { Routes, Route, Navigate } from "react-router-dom"
import { useAuth } from "./context/AuthContext"
import { useState } from "react"
import ScrollToTop from "./components/common/ScrollToTop"

// Layout
import Header from "./components/common/header/Header"
import Footer from "./components/common/footer/Footer"

// Auth modal
import AuthModal  from "./components/auth/AuthModal"
import AdminLogin from "./components/auth/AdminLogin"

// Marketing pages
import Home       from "./components/home/Home"
import About      from "./components/about/About"
import CourseHome from "./components/allcourses/CourseHome"
import Team       from "./components/team/Team"
import Pricing    from "./components/pricing/Pricing"
import Blog       from "./components/blog/Blog"
import Contact    from "./components/contact/Contact"

// Feature detail pages
import PracticalTrading    from "./components/features/PracticalTrading"
import TradingCertificate  from "./components/features/TradingCertificate"
import Mentorship          from "./components/features/Mentorship"

// Student
import Dashboard    from "./components/dashboard/Dashboard"
import CoursePlayer from "./components/dashboard/CoursePlayer"

// Admin
import AdminDashboard    from "./components/admin/AdminDashboard"
import AdminCourses      from "./components/admin/AdminCourses"
import AdminStudents     from "./components/admin/AdminStudents"
import AdminPayments     from "./components/admin/AdminPayments"
import AdminContacts     from "./components/admin/AdminContacts"
import AdminEnrollments  from "./components/admin/AdminEnrollments"
import AdminLiveSessions from "./components/admin/AdminLiveSessions"
import LiveSessions      from "./components/live/LiveSessions"

/* ── Loading spinner ── */
const Spinner = () => (
  <div style={{
    minHeight: "100vh", display: "flex", alignItems: "center",
    justifyContent: "center", background: "#0f1f1e", color: "#c9a227",
    flexDirection: "column", gap: 16
  }}>
    <i className="fa fa-spinner fa-spin" style={{ fontSize: 36 }}></i>
    <span style={{ fontSize: 14, opacity: 0.6 }}>Loading…</span>
  </div>
)

/* ── Protected student route — shows modal if not logged in ── */
const ProtectedUser = ({ children }) => {
  const { isUser, loading } = useAuth()
  const [modal, setModal]   = useState(!isUser && !loading)

  if (loading) return <Spinner />

  if (!isUser) {
    return (
      <>
        {/* Show home page blurred behind the modal */}
        <div style={{ filter: "blur(3px)", pointerEvents: "none", userSelect: "none" }}>
          <Header />
          <Home />
          <Footer />
        </div>
        <AuthModal
          isOpen={true}
          onClose={() => setModal(false)}
          mode="login"
        />
      </>
    )
  }
  return children
}

/* ── Protected admin route ── */
const ProtectedAdmin = ({ children }) => {
  const { isAdmin, loading } = useAuth()
  if (loading) return <Spinner />
  return isAdmin ? children : <Navigate to="/ra-control-panel" replace />
}

/* ── Marketing pages always have Header + Footer ── */
const MarketingLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
)

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>

      {/* ── Public marketing pages ── */}
      <Route path="/"        element={<MarketingLayout><Home       /></MarketingLayout>} />
      <Route path="/about"   element={<MarketingLayout><About      /></MarketingLayout>} />
      <Route path="/live"    element={<MarketingLayout><LiveSessions /></MarketingLayout>} />
      <Route path="/courses" element={<MarketingLayout><CourseHome /></MarketingLayout>} />
      <Route path="/team"    element={<MarketingLayout><Team       /></MarketingLayout>} />
      <Route path="/pricing" element={<MarketingLayout><Pricing    /></MarketingLayout>} />
      <Route path="/journal" element={<MarketingLayout><Blog       /></MarketingLayout>} />
      <Route path="/contact" element={<MarketingLayout><Contact    /></MarketingLayout>} />

      {/* Feature detail pages */}
      <Route path="/features/practical-trading"   element={<MarketingLayout><PracticalTrading   /></MarketingLayout>} />
      <Route path="/features/trading-certificate" element={<MarketingLayout><TradingCertificate /></MarketingLayout>} />
      <Route path="/features/mentorship"          element={<MarketingLayout><Mentorship         /></MarketingLayout>} />

      {/* /login and /register redirect to home — modal opens from Header */}
      <Route path="/login"    element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />

      {/* ── Admin portal — secret URL only company knows ── */}
      <Route path="/ra-control-panel" element={<AdminLogin />} />

      {/* ── Student protected pages ── */}
      <Route path="/dashboard" element={
        <ProtectedUser><Dashboard /></ProtectedUser>
      } />
      <Route path="/courses/:slug/learn" element={
        <ProtectedUser><CoursePlayer /></ProtectedUser>
      } />

      {/* ── Admin protected pages ── */}
      <Route path="/admin/dashboard"    element={<ProtectedAdmin><AdminDashboard    /></ProtectedAdmin>} />
      <Route path="/admin/courses"      element={<ProtectedAdmin><AdminCourses      /></ProtectedAdmin>} />
      <Route path="/admin/students"     element={<ProtectedAdmin><AdminStudents     /></ProtectedAdmin>} />
      <Route path="/admin/payments"     element={<ProtectedAdmin><AdminPayments     /></ProtectedAdmin>} />
      <Route path="/admin/contacts"     element={<ProtectedAdmin><AdminContacts     /></ProtectedAdmin>} />
      <Route path="/admin/enrollments"  element={<ProtectedAdmin><AdminEnrollments  /></ProtectedAdmin>} />
      <Route path="/admin/live-sessions"element={<ProtectedAdmin><AdminLiveSessions /></ProtectedAdmin>} />

      {/* ── 404 → home ── */}
      <Route path="*" element={<Navigate to="/" replace />} />

    </Routes>
    </>
  )
}

export default App
